/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Helpers for custom-object image public/private upload routing.
 * Public images go to pearlcrm-{env}-public; private stay on pearlcrm-{env}.
 */

export function isPublicBucketName(bucketName) {
  const name = String(bucketName || '').trim().toLowerCase();
  return name.endsWith('-public');
}

/**
 * Infer whether an existing or staged image value should be treated as public.
 * Defaults to public for new uploads when no durable metadata is present.
 */
export function inferImageIsPublic(value) {
  if (value == null) return true;
  if (typeof value === 'string') {
    const text = value.trim().toLowerCase();
    if (!text) return true;
    // Presigned private URLs include AWS signature query params.
    if (text.includes('x-amz-signature') || text.includes('x-amz-credential')) {
      return false;
    }
    return true;
  }
  if (Array.isArray(value)) {
    if (!value.length) return true;
    const first = value[0];
    if (typeof first === 'string') return inferImageIsPublic(first);
    if (typeof first?.is_public === 'boolean') return first.is_public;
    return isPublicBucketName(first?.bucket_name || first?.bucket);
  }
  if (typeof value.is_public === 'boolean') return value.is_public;
  if (value.bucket_name || value.bucket) {
    return isPublicBucketName(value.bucket_name || value.bucket);
  }
  if (typeof value.url === 'string') return inferImageIsPublic(value.url);
  return true;
}

/**
 * Feature path used for R2 object keys when uploading custom-object images.
 * Comic Pages keep the dedicated lively path; everything else is namespaced
 * by custom object api name.
 */
export function resolveImageFeaturePath(apiName) {
  const normalized = String(apiName || '').trim().toLowerCase();
  if (normalized === 'comic_page') return 'lively/comic-pages';
  if (normalized) return `custom-objects/${normalized}/images`;
  return 'custom-objects/images';
}

/**
 * Annotate a staged multi-image batch with the current public/private choice
 * so save-time routing can read it without extra form state.
 */
export function withBatchPrivacy(images, isPublic) {
  return (images || []).map((image) => ({
    ...image,
    is_public: !!isPublic,
  }));
}
